import {
    getAppliedRefiners,
    getSuggestedRefiners,
    lazyApplyBooleanRefiner,
    lazyRemoveRefiner,
} from 'owa-search-refiners';
import { SearchScenarioId } from 'owa-search-store';
import type { BooleanRefiner, Refiner } from 'owa-search-types';
import {
    startSearch,
    lazyResetSuggestionsQuickActionsMutator,
    exitSearch,
} from 'owa-search-actions';
import type { SearchTab } from 'owa-search-types/lib/types/SearchTab';
import { lazySetActiveSearchTab, getActiveSearchTab } from 'owa-tabbed-search';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getSelectedTableView } from 'owa-mail-list-store';
import { isCopilotResultsTable } from 'owa-mail-list-store/lib/store/schema/CopilotResultsTableQuery';
import { lazyDismissCopilotResultsTable } from 'owa-mail-table-loading-actions';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { owaComputedFn } from 'owa-computed-fn';

export const disableSearchRefinersOnInterplay: () => boolean = owaComputedFn(() =>
    isCopilotResultsTable(getSelectedTableView()?.tableQuery)
);

export const getExitSearchAction = (): void => {
    const tableView = getSelectedTableView();
    if (isCopilotResultsTable(tableView?.tableQuery)) {
        lazyDismissCopilotResultsTable.importAndExecute('ExitSearch', getMailboxInfo(tableView));
        return;
    }
    exitSearch('SearchRibbon', SearchScenarioId.Mail);
};

export const getSearchRefinerAction = (refinerName: string): void => {
    const refiner = getRefiner(refinerName);
    if (refiner?.appliedRefiner) {
        lazyRemoveRefiner.import().then(removeRefinerAction => {
            if (refiner.appliedRefiner) {
                removeRefinerAction(SearchScenarioId.Mail, refiner.appliedRefiner);
                startSearch('SearchRefiner', SearchScenarioId.Mail, false /* explicitSearch */);
            }
        });
    } else if (refiner?.suggestedRefiner) {
        lazyApplyBooleanRefiner.import().then(applyRefinerAction => {
            applyRefinerAction(SearchScenarioId.Mail, refiner.suggestedRefiner as BooleanRefiner);
            startSearch('SearchRefiner', SearchScenarioId.Mail, false /* explicitSearch */);
        });
    }
};

export const getSearchEntityAction = (entityName: string): void => {
    if (isGlobalFeatureEnabled('sea-deprecateSerpTabs')) {
        return;
    }
    lazySetActiveSearchTab.importAndExecute(entityName as SearchTab).then(() => {
        lazyResetSuggestionsQuickActionsMutator.importAndExecute(SearchScenarioId.Mail).then(() => {
            startSearch(
                'TabbedSearchPivot-' + entityName,
                SearchScenarioId.Mail,
                true /* explicitSearch */
            );
        });
    });
};

export const isRefinerApplied = (refinerName: string): boolean => {
    const appliedRefiners = getAppliedRefiners(SearchScenarioId.Mail);
    return appliedRefiners.some(refiner => refiner.name === refinerName);
};

export const isSearchEntityFilterSelected = (tabName: string): boolean => {
    const selectedTab = getActiveSearchTab();
    if (!selectedTab) {
        return false;
    }
    return selectedTab === (tabName as SearchTab);
};

export const getRefiner = (
    refinerName: string
): {
    suggestedRefiner: Refiner | undefined;
    appliedRefiner: Refiner | undefined;
} => {
    const suggestedRefiners = getSuggestedRefiners(SearchScenarioId.Mail);
    const suggestedRefiner = suggestedRefiners.find(refiner => refiner.name === refinerName);

    const appliedRefiners = getAppliedRefiners(SearchScenarioId.Mail);
    const appliedRefiner = appliedRefiners.find(refiner => refiner.name === refinerName);

    return { suggestedRefiner, appliedRefiner };
};
